import type { SessionRegistrationAttributes, UserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import { LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER } from '@/client/services/learnerFeedback/constants'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import {
  SESSION_FEEDBACK_TYPE_TUTOR,
  type SessionRegistrationType,
  type TutorSessionFeedbackRating,
  type Viewpoint,
} from './constants'
import LearnerFeedbackQ from './LearnerFeedbackQ'
import SessionAttendeeQ from './SessionAttendeeQ'
import SessionFeedbackQ from './SessionFeedbackQ'
import SessionQ from './SessionQ'
import TopicQ from './TopicQ'
import UserQ from './UserQ'

const SessionRegistrationQ = {
  ...makeQueryModelV2<SessionRegistrationAttributes>('session_registration')([
    'id',
    'session_id',
    'session_breakout_id',
    'user_id',
    'details',
    'join_url',
    'snooze_feedback_until',
    'attempted_joining',
    'zoom_email_hash',
    'excused_absence_source',
    'excused_absence_reason',
    'type',
    'internal_state',
    'created_at',
    'deleted_at',
  ]),

  where: (
    {
      id,
      paranoid = true,
      sessionId,
      sessionBreakoutId = null,
      userId,
      attemptedJoining,
      type,
      includeSession,
      includeTopic,
      includeUser,
      includeSessionFeedback,
      includeLearnerFeedback,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      paranoid?: boolean
      sessionId?: number | null | SQLFragment<number>
      sessionBreakoutId?: number | boolean | SQLFragment<number> | null
      userId?: number | null | SQLFragment<number>
      attemptedJoining?: boolean | null
      type?: SessionRegistrationType
      includeSession?: boolean
      includeTopic?: boolean
      includeUser?: boolean
      includeSessionFeedback?: boolean
      includeLearnerFeedback?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionRegistrationAttributes> => SQL`
            FROM ${SessionRegistrationQ}
            ${
              includeSession === true || includeTopic === true
                ? SQL`INNER JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionRegistrationQ.session_id}`
                : SQL``
            }
            ${
              includeTopic === true
                ? SQL`INNER JOIN ${TopicQ} ON ${SessionQ.topic_id} = ${TopicQ.id}`
                : SQL``
            }
            ${
              includeUser === true
                ? SQL`INNER JOIN ${UserQ} ON ${UserQ.id} = ${SessionRegistrationQ.user_id}`
                : SQL``
            }
            ${
              includeSessionFeedback === true
                ? SQL`LEFT JOIN ${SessionFeedbackQ}
                            ON ${SessionFeedbackQ.session_id} = ${SessionRegistrationQ.session_id}
                            AND ${SessionFeedbackQ.user_id} = ${SessionRegistrationQ.user_id}`
                : SQL``
            }
            ${
              includeLearnerFeedback === true
                ? SQL`LEFT JOIN ${LearnerFeedbackQ}
                            ON ${LearnerFeedbackQ.session_id} = ${SessionRegistrationQ.session_id}
                            AND ${LearnerFeedbackQ.reviewee_user_id} = ${SessionRegistrationQ.user_id}`
                : SQL``
            }
            ${includeAssociationsToSql(SessionRegistrationQ, include)}
            WHERE
                ${id != null ? SQL`${SessionRegistrationQ.id} = ${id} AND` : SQL``}
                ${sessionId != null ? SQL`${SessionRegistrationQ.session_id} = ${sessionId} AND` : SQL``}
                ${
                  sessionBreakoutId == null
                    ? SQL`${SessionRegistrationQ.session_breakout_id} IS NULL AND `
                    : typeof sessionBreakoutId === 'boolean'
                      ? SQL`${SessionRegistrationQ.session_breakout_id} IS ${sessionBreakoutId ? SQL`NOT` : SQL``} NULL AND`
                      : SQL`${SessionRegistrationQ.session_breakout_id} = ${sessionBreakoutId} AND`
                }
                ${userId != null ? SQL`${SessionRegistrationQ.user_id} = ${userId} AND` : SQL``}
                ${
                  attemptedJoining != null
                    ? SQL`${SessionRegistrationQ.attempted_joining} = ${attemptedJoining} AND`
                    : SQL``
                }
                ${type != null ? SQL`${SessionRegistrationQ.type} = ${type} AND` : SQL``}
                ${
                  includeLearnerFeedback === true
                    ? SQL`${LearnerFeedbackQ.type} = ${LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER} AND`
                    : SQL``
                }
                ${paranoid ? SQL`${SessionRegistrationQ.deleted_at} IS NULL AND` : SQL``}
                ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryGroupByToSQL(groupBy)}
            ${queryOrderToSQL(order)}
            ${queryLimitToSQL(limit)}
        `,

  getZoomAttendanceSeconds: (): SQLFragment<number> =>
    SessionAttendeeQ.getZoomAttendanceSeconds({
      userId: SessionRegistrationQ.user_id,
      sessionId: SessionRegistrationQ.session_id,
    }),

  getZoomChatCount: (): SQLFragment<number> =>
    SessionAttendeeQ.getZoomChatCount({
      userId: SessionRegistrationQ.user_id,
      sessionId: SessionRegistrationQ.session_id,
    }),

  getZoomSpeakingDurationSeconds: (): SQLFragment<number> =>
    SessionAttendeeQ.getZoomSpeakingDurationSeconds({
      userId: SessionRegistrationQ.user_id,
      sessionId: SessionRegistrationQ.session_id,
    }),

  attended: (): SQLFragment<number> =>
    SQL`EXISTS(SELECT 1
    ${SessionAttendeeQ.where({
      userId: SessionRegistrationQ.user_id,
      sessionId: SessionRegistrationQ.session_id,
    })})`,

  hasFeedback: (rating?: TutorSessionFeedbackRating): SQLFragment<number> =>
    SQL`EXISTS(SELECT 1 ${SessionFeedbackQ.where({
      userId: SessionRegistrationQ.user_id,
      sessionId: SessionRegistrationQ.session_id,
      rating,
      type: SESSION_FEEDBACK_TYPE_TUTOR,
    })})`,

  hasCompletedSessionSurvey: (): SQLFragment<boolean> =>
    SQL`${SessionRegistrationQ.internal_state}->'session_survey_completed_at' IS NOT NULL`,

  associations: (): AssociationDefinitions => ({
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: SessionRegistrationQ.session_id,
    },
    User: { query: UserQ, foreignKey: UserQ.id, sourceKey: SessionRegistrationQ.user_id },
  }),

  getUser: (): SQLFragment<UserAttributes> =>
    SQL`${UserQ.where({
      id: SessionRegistrationQ.user_id,
    })}`,

  getViewpoint: (): SQLFragment<Viewpoint> =>
    SQL`${SessionRegistrationQ.internal_state}->>'viewpoint'`,
}

export default SessionRegistrationQ
